import { useState } from 'react';
import { MapPin, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useArchiveInterventionZone,
  useCreateInterventionZone,
  useInterventionZones,
  useUpdateInterventionZone,
} from '../hooks/use-dossier-company';
import type {
  CompanyInterventionZone,
  InterventionZonePriority,
  InterventionZoneType,
} from '../types/dossier-company.types';

/**
 * Sprint 5b.1 v2 — Phase C.4a : section Zones d'intervention.
 *
 * UI dédiée par type (COMMUNE/DEPARTEMENT/REGION/PAYS/RAYON_KM) avec saisie polymorphique.
 * Validation côté backend → toast erreur si format invalide.
 * Soft limit 50 zones (warning UI).
 */

const TYPE_LABELS: Record<InterventionZoneType, string> = {
  COMMUNE: 'Commune',
  DEPARTEMENT: 'Département',
  REGION: 'Région',
  PAYS: 'Pays',
  RAYON_KM: 'Rayon (km)',
};

const TYPE_HELP: Record<InterventionZoneType, string> = {
  COMMUNE: 'Identifiant City (entier, ex. 42501)',
  DEPARTEMENT: 'Code département (75, 2A, 971...)',
  REGION: 'Code région (11, 84, 76...)',
  PAYS: 'Code ISO 3166-1 alpha-2 majuscule (FR, BE, DE...)',
  RAYON_KM: 'Rayon en km autour du siège (1-5000)',
};

const PRIORITY_LABELS: Record<InterventionZonePriority, string> = {
  PRIMARY: 'Principale',
  SECONDARY: 'Secondaire',
};

const SOFT_LIMIT = 50;

export function InterventionZonesSection() {
  const { data: zones, isLoading } = useInterventionZones(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }

  const zonesList = zones ?? [];
  const grouped = groupByType(zonesList);
  const overSoftLimit = zonesList.length > SOFT_LIMIT;

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Zones d'intervention
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Où votre company intervient géographiquement. Utilisé par la checklist GO et le
            sourcing automatique d'AO.
          </p>
        </div>
        <Button size="sm" onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Ajouter une zone
        </Button>
      </div>

      {overSoftLimit && (
        <p className="text-xs text-amber-700 mb-3">
          ⚠ {zonesList.length} zones définies — au-delà de {SOFT_LIMIT} le matching peut devenir bruité.
        </p>
      )}

      {zonesList.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          Aucune zone définie. Ajoutez au moins une zone principale.
        </p>
      ) : (
        <div className="space-y-4">
          {(Object.keys(grouped) as InterventionZoneType[]).map((t) => {
            const items = grouped[t];
            if (!items || items.length === 0) return null;
            return (
              <div key={t}>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                  {TYPE_LABELS[t]} ({items.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {items.map((z) => (
                    <ZoneTag key={z.id} zone={z} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AddZoneDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </Card>
  );
}

function groupByType(
  zones: CompanyInterventionZone[],
): Partial<Record<InterventionZoneType, CompanyInterventionZone[]>> {
  const out: Partial<Record<InterventionZoneType, CompanyInterventionZone[]>> = {};
  for (const z of zones) {
    if (!out[z.type]) out[z.type] = [];
    out[z.type]!.push(z);
  }
  return out;
}

function ZoneTag({ zone }: { zone: CompanyInterventionZone }) {
  const update = useUpdateInterventionZone();
  const archive = useArchiveInterventionZone();

  function togglePriority() {
    const next: InterventionZonePriority =
      zone.priority === 'PRIMARY' ? 'SECONDARY' : 'PRIMARY';
    update.mutate({ zoneId: zone.id, input: { priority: next } });
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border bg-secondary px-2 py-1 text-xs">
      <span>{zone.label ?? zone.value}</span>
      <button
        type="button"
        onClick={togglePriority}
        title={`Basculer ${PRIORITY_LABELS[zone.priority === 'PRIMARY' ? 'SECONDARY' : 'PRIMARY']}`}
        className="hover:opacity-70"
        disabled={update.isPending}
      >
        <Badge
          variant={zone.priority === 'PRIMARY' ? 'default' : 'outline'}
          className="h-4 text-[10px] px-1.5"
        >
          {PRIORITY_LABELS[zone.priority]}
        </Badge>
      </button>
      <button
        type="button"
        onClick={() => archive.mutate(zone.id)}
        className="ml-0.5 rounded hover:bg-destructive/20 hover:text-destructive"
        aria-label={`Archiver ${zone.label ?? zone.value}`}
        disabled={archive.isPending}
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </span>
  );
}

function AddZoneDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [type, setType] = useState<InterventionZoneType>('DEPARTEMENT');
  const [value, setValue] = useState('');
  const [priority, setPriority] = useState<InterventionZonePriority>('PRIMARY');
  const create = useCreateInterventionZone();

  async function handleSubmit() {
    if (!value.trim()) return;
    try {
      await create.mutateAsync({ type, value: value.trim(), priority });
      setValue('');
      onClose();
    } catch {
      // toast géré par le hook
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajouter une zone d'intervention</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="zone-type">Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as InterventionZoneType)}>
              <SelectTrigger id="zone-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(TYPE_LABELS) as InterventionZoneType[]).map((t) => (
                  <SelectItem key={t} value={t}>
                    {TYPE_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{TYPE_HELP[type]}</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="zone-value">Valeur</Label>
            <Input
              id="zone-value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={
                type === 'COMMUNE'
                  ? '42501'
                  : type === 'DEPARTEMENT'
                    ? '75'
                    : type === 'REGION'
                      ? '11'
                      : type === 'PAYS'
                        ? 'FR'
                        : '250'
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="zone-priority">Priorité</Label>
            <Select
              value={priority}
              onValueChange={(v) => setPriority(v as InterventionZonePriority)}
            >
              <SelectTrigger id="zone-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PRIMARY">Principale</SelectItem>
                <SelectItem value="SECONDARY">Secondaire</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={!value.trim() || create.isPending}>
            Ajouter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
